import type { FieldPath, FieldValues, UseFormSetError } from 'react-hook-form'

export interface ServerFieldViolation {
  field: string
  rule: string
  description?: string
}

interface RawViolation {
  field?: unknown
  rule?: unknown
  description?: unknown
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object'

/** When rule is empty, map description to an i18n key segment (e.g. "already taken" → already_taken). */
function ruleKeyFromDescription(description: string): string {
  const key = description
    .trim()
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, '_')
    .replaceAll(/^_+|_+$/g, '')
  return key || 'unknown'
}

const normalizeViolations = (raw: unknown): ServerFieldViolation[] => {
  if (!Array.isArray(raw)) {
    return []
  }
  return (raw as RawViolation[]).flatMap<ServerFieldViolation>(item => {
    if (!isRecord(item)) {
      return []
    }
    const field = typeof item.field === 'string' ? item.field : ''
    if (!field) {
      return []
    }
    let rule = typeof item.rule === 'string' ? item.rule.trim() : ''
    const description =
      typeof item.description === 'string' ? item.description : undefined
    if (!rule && description) {
      rule = ruleKeyFromDescription(description)
    }
    if (!rule) {
      return []
    }
    return [{ field, rule, description }]
  })
}

export const extractServerViolations = (
  error: unknown
): ServerFieldViolation[] => {
  if (!isRecord(error)) {
    return []
  }

  const axiosViolations = (
    error as { response?: { data?: { violations?: unknown } } }
  ).response?.data?.violations
  const fetchViolations = (error as { error?: { violations?: unknown } }).error
    ?.violations
  const directViolations = (error as { violations?: unknown }).violations

  const raw = [axiosViolations, fetchViolations, directViolations].find(v =>
    Array.isArray(v)
  )
  return normalizeViolations(raw)
}

// OCE-656 (follow-up): аналог extractApiErrorCodeAsync из mapApiError.ts.
// PUT /api/v2/users/me в сгенерированном swagger-typescript-api-клиенте
// объявлен с response-body `void`, поэтому при 4xx клиент пробрасывает
// **сырой Response** с ещё не прочитанным JSON-телом. Синхронный
// extractServerViolations для такого входа возвращает [] — и per-field
// ошибки (например, format violation из user-profiles UpdateUser, OCE-552)
// не применяются к react-hook-form. extractServerViolationsAsync
// вычитывает body один раз и достаёт оттуда violations[].
export const extractServerViolationsAsync = async (
  error: unknown
): Promise<ServerFieldViolation[]> => {
  const direct = extractServerViolations(error)
  if (direct.length > 0) return direct

  if (typeof Response !== 'undefined' && error instanceof Response) {
    const contentType = error.headers.get('content-type') || ''
    if (!contentType.includes('application/json')) return []
    if (error.bodyUsed) return []
    try {
      const body = await error.clone().json()
      return normalizeViolations((body as { violations?: unknown })?.violations)
    } catch {
      return []
    }
  }

  return []
}

export interface ApplyServerViolationsOptions<TValues extends FieldValues> {
  setError: UseFormSetError<TValues>
  fieldMap: Partial<Record<string, FieldPath<TValues>>>
  ruleKeyPrefix: string
  focusFirst?: boolean
}

export interface ApplyServerViolationsResult {
  appliedFields: string[]
  unmapped: ServerFieldViolation[]
}

export const applyServerViolations = <TValues extends FieldValues>(
  violations: ServerFieldViolation[],
  options: ApplyServerViolationsOptions<TValues>
): ApplyServerViolationsResult => {
  const appliedFields: string[] = []
  const unmapped: ServerFieldViolation[] = []
  let isFirstApplied = true

  for (const violation of violations) {
    const formField = options.fieldMap[violation.field]
    if (!formField) {
      unmapped.push(violation)
      continue
    }

    options.setError(
      formField,
      {
        type: 'server',
        message: `${options.ruleKeyPrefix}.${violation.rule}`
      },
      options.focusFirst && isFirstApplied ? { shouldFocus: true } : undefined
    )
    appliedFields.push(formField)
    isFirstApplied = false
  }

  return { appliedFields, unmapped }
}
